import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Skeleton } from "#/components/primitives";
import { formatMoney } from "#/lib/medusa-types/commerce";
import { notify } from "#/lib/notify";
import { ROUTES } from "#/lib/routes";
import { useMyOrders } from "#/queries/orders";
import { useReorderMutation } from "#/queries/reorder";
import { useCartStore } from "#/store/cart";

export const Route = createFileRoute("/account/orders")({
  head: () => ({ meta: [{ title: "My Orders" }] }),
  component: OrdersTab,
});

function OrdersTab() {
  const { data, isPending, isError } = useMyOrders();
  const setCartId = useCartStore((s) => s.setCartId);
  const navigate = useNavigate();
  const reorder = useReorderMutation();

  const handleReorder = (orderId: string) => {
    reorder.mutate(orderId, {
      onSuccess: (res) => {
        setCartId(res.cart_id);
        if (res.dropped && res.dropped.length > 0) {
          notify.info("Some items were unavailable");
        }
        void navigate({ to: ROUTES.cart });
      },
      onError: () => {
        notify.error("Couldn't reorder");
      },
    });
  };

  if (isPending) {
    return (
      <div
        className="flex flex-col gap-6"
        aria-label="Loading orders"
        role="status"
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="flex items-center justify-between gap-6 rounded-2xl border border-lp-grey/60 bg-white p-6"
          >
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-56" />
              <Skeleton className="h-4 w-48" />
            </div>
            <div className="flex flex-col items-end gap-2">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-4 w-12" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-12 text-center font-display text-lp-text-lt">
        Sign in to see your orders.
      </div>
    );
  }

  const orders = data?.orders ?? [];

  if (orders.length === 0) {
    return (
      <div className="py-12 text-center font-display text-lp-text-lt">
        You haven't placed any orders yet.{" "}
        <Link
          to={ROUTES.shop}
          className="text-lp-green-mid underline hover:text-lp-green-dk"
        >
          Browse the shop
        </Link>
        .
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {orders.map((order) => {
        const placedAt = order.created_at
          ? new Date(order.created_at).toLocaleDateString("en-PH", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "";
        const itemCount = (order.items ?? []).reduce(
          (sum, item) => sum + Number(item.quantity ?? 0),
          0,
        );
        return (
          <div
            key={order.id}
            className="flex items-center justify-between gap-6 rounded-2xl border border-lp-grey/60 bg-white p-6"
          >
            <div className="min-w-0">
              <div className="font-display font-semibold text-lp-green-dk text-lg">
                Order #{order.display_id ?? order.id.slice(0, 8)}
              </div>
              <div className="mt-1 font-display text-lp-text-lt text-sm">
                {placedAt} · {itemCount} {itemCount === 1 ? "item" : "items"}
              </div>
              <div className="mt-1 font-display text-lp-text text-sm capitalize">
                {order.status} · {order.fulfillment_status} · {order.payment_status}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="font-mono text-lg text-lp-text">
                {formatMoney(Number(order.total ?? 0), order.currency_code)}
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/track"
                  className="font-display text-lp-green-mid text-sm underline hover:text-lp-green-dk"
                >
                  Track
                </Link>
                <button
                  type="button"
                  onClick={() => handleReorder(order.id)}
                  disabled={reorder.isPending && reorder.variables === order.id}
                  className="cursor-pointer rounded-full border-0 bg-lp-green-mid px-4 py-1.5 font-display font-semibold text-sm text-white hover:bg-lp-green-dk disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {reorder.isPending && reorder.variables === order.id
                    ? "Reordering…"
                    : "Reorder"}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
